import {
  fetchPeople,
  fetchProjects,
  fetchPublications,
  fetchNews,
  fetchWorkAreas,
  fetchUtilities,
} from './api';
import {
  extractPeopleData,
  extractProjectWorkAreas,
  sortByYear,
} from './dataTransform';

export const ALL_KEYS = [
  'people',
  'projects',
  'publications',
  'news',
  'workAreas',
  'utilities',
];

const FETCHERS = {
  people: fetchPeople,
  projects: fetchProjects,
  publications: fetchPublications,
  news: fetchNews,
  workAreas: fetchWorkAreas,
  utilities: fetchUtilities,
};

const TRANSFORMERS = {
  people: extractPeopleData,
  projects: extractProjectWorkAreas,
  publications: sortByYear,
  news: (data) => data,
  workAreas: (data) => data,
  utilities: (data) => data,
};

/** Route-aware critical datasets for first paint. */
export function getCriticalKeys(pathname) {
  const path = pathname.replace(/\/$/, '') || '/';

  if (path === '/') {
    return ['workAreas', 'people', 'projects'];
  }
  if (path.startsWith('/projects')) {
    return ['projects', 'people', 'workAreas'];
  }
  if (path.startsWith('/people') || path.startsWith('/team')) {
    return ['people', 'projects', 'workAreas', 'publications'];
  }
  if (path.startsWith('/workareas')) {
    return ['workAreas', 'projects', 'people'];
  }
  if (path.startsWith('/news')) {
    return ['news', 'utilities'];
  }
  if (path.startsWith('/publications')) {
    return ['publications', 'utilities'];
  }
  // static utility pages
  if (
    path.startsWith('/thelab') ||
    path.startsWith('/impressum') ||
    path.startsWith('/dataprotection') ||
    path.startsWith('/accessibility')
  ) {
    return ['utilities'];
  }

  // fallback / unknown
  return ['workAreas', 'people', 'projects'];
}

export function getBackgroundKeys(criticalKeys) {
  const critical = new Set(criticalKeys);
  return ALL_KEYS.filter((key) => !critical.has(key));
}

export async function fetchDataset(key) {
  const raw = await FETCHERS[key]();
  const transform = TRANSFORMERS[key];
  return transform(raw);
}

/** Same column split as RotatorWrapper — first visible frame per column. */
export function splitRotatorColumns(cardArray, type) {
  if (!cardArray || cardArray.length === 0) {
    return [[], [], []];
  }

  let summedArray = cardArray.concat(cardArray);
  if (type === 'workAreas' || type === 'projects') {
    summedArray = summedArray.concat(cardArray);
  }

  const remainder = summedArray.length % 3;
  if (remainder !== 0) {
    summedArray = summedArray.slice(0, summedArray.length - remainder);
  }

  const left = [];
  const center = [];
  const right = [];
  const boxes = [left, center, right];
  let boxIndex = 0;
  for (let i = 0; i < summedArray.length; i++) {
    boxes[boxIndex].push(summedArray[i]);
    boxIndex = (boxIndex + 1) % 3;
  }
  return [left, center, right];
}

function imageUrlForCard(card, type) {
  if (!card?.acf) return null;
  if (type === 'workAreas') return card.acf.imgUrl?.url || null;
  if (type === 'people') return card.acf.photourl?.url || null;
  if (type === 'projects') return card.acf.projectimg?.url || null;
  return null;
}

/** First visible URL in each of the 3 columns for WA / people / projects (up to 9). */
export function getHomeRotatorPreloadUrls({ workAreas, people, projects }) {
  const urls = [];
  const sections = [
    { data: workAreas, type: 'workAreas' },
    { data: people, type: 'people' },
    { data: projects, type: 'projects' },
  ];

  sections.forEach(({ data, type }) => {
    const [left, center, right] = splitRotatorColumns(data || [], type);
    [left[0], center[0], right[0]].forEach((card) => {
      const url = imageUrlForCard(card, type);
      if (url) urls.push(url);
    });
  });

  return [...new Set(urls)];
}

/** Preload image URLs; resolves when all settle or timeout elapses. */
export function preloadImages(urls, timeoutMs = 8000) {
  if (!urls.length) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = 0;
    const total = urls.length;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve();
    };

    const onOne = () => {
      settled += 1;
      if (settled >= total) finish();
    };

    const timer = setTimeout(finish, timeoutMs);

    urls.forEach((url) => {
      const img = new window.Image();
      img.onload = onOne;
      img.onerror = onOne;
      img.src = url;
    });
  });
}
